/**
 * A class for validating and sanitizing user data for text boxes.
 * @category Components
 * @subcategory TextBox
 */
export default class TextBoxDataValidator {
  /**
   * @param {Object} config - The default configuration for validation.
   */
  constructor(config) {
    /**
     * Default configuration object.
     * @type {Object}
     */
    this.defaultConfig = config;
  }

  cleanup() {
    this.defaultConfig = null;
    console.log("✅ TextBoxDataValidator cleaned up.");
}

  // Validate and sanitize user data

  /**
   * Validates and sanitizes user data based on the default configuration.
   * @param {Object} userData - The user data to validate.
   * @returns {Object} The validated and sanitized data.
   */
  validate(userData) {
    const defaultData = this.defaultConfig.others[0];

    const validatedData = {
      objectId: this._validateProperty(
        userData.objectId,
        defaultData.objectId,
        "string"
      ),  
      mode: this._validateProperty(userData.mode, defaultData.mode, "string"), // Ensure mode is not missing
      text: this._validateProperty(userData.text, defaultData.text, "string"),
      bold: this._validateProperty(userData.bold, defaultData.bold, "boolean"),
      outlineColor: this._validateProperty(
        userData.outlineColor,
        defaultData.outlineColor,
        "color"
      ),
      textColor: this._validateProperty(
        userData.textColor,
        defaultData.textColor,
        "color"
      ),
      bgColor: this._validateProperty(
        userData.bgColor,
        defaultData.bgColor,
        "color"
      ),
      fontSize: this._validateProperty(
        userData.fontSize,
        defaultData.fontSize,
        "number",
        1,
        100
      ), // Add range check for fontSize (1-100)
      alpha: this._validateProperty(
        userData.alpha,
        defaultData.alpha,
        "number",
        0,
        1
      ), // Ensure alpha is between 0 and 1
      fontFamily: this._validateProperty(
        userData.fontFamily,
        defaultData.fontFamily,
        "string"
      ),
      w: this._validateProperty(userData.w, defaultData.w, "number", 0.1), // Add minimum width check
      h: this._validateProperty(userData.h, defaultData.h, "number", 0.1), // Add minimum height check
      x: this._validateProperty(userData.x, defaultData.x, "number"),
      y: this._validateProperty(userData.y, defaultData.y, "number"),
      z: this._validateProperty(userData.z, defaultData.z, "number"),
      angle: this._validateProperty(
        userData.angle,
        defaultData.angle,
        "number"
      ),
    };

    if (
      (!validatedData.bgColor || this._isColorWhite(validatedData.bgColor)) &&
      this._isColorWhite(validatedData.textColor)
    ) {
      validatedData.textColor = { red: 0, green: 0, blue: 0 };
    }

    return validatedData;
  }

  /**
   * Checks if a color object represents white.
   * @param {Object} color - The color object with red, green, and blue properties.
   * @returns {boolean} `true` if the color is white, otherwise `false`.
   */
  _isColorWhite(color) {
    return (
      color && color.red === 255 && color.green === 255 && color.blue === 255
    );
  }

  /**
   * Validates a property based on its type and constraints.
   * @param {*} value - The value to validate.
   * @param {*} defaultValue - The default value to return if validation fails.
   * @param {string} type - The type of the property (e.g., "string", "number", "color").
   * @param {number} [min] - The minimum allowable value (for numbers).
   * @param {number} [max] - The maximum allowable value (for numbers).
   * @returns {*} The validated value, or the default value if validation fails.
   */
  _validateProperty(value, defaultValue, type, min, max) {
    if (value === undefined || value === null) return defaultValue;

    switch (type) {
      case "color":
        return this._isValidColor(value) ? value : defaultValue;
      case "boolean":
        return typeof value === "boolean" ? value : defaultValue;
      case "number":
        if (typeof value === "number" && !isNaN(value)) {
          if (min !== undefined && value < min) return defaultValue; // Ensure value is above min
          if (max !== undefined && value > max) return defaultValue; // Ensure value is below max
          return value;
        }
        return defaultValue;
      case "string":
        return typeof value === "string" && value.trim() !== ""
          ? value
          : defaultValue;
      default:
        return defaultValue;
    }
  }

  /**
   * Validates if an object represents a valid color.
   * @param {Object} color - The color object with red, green, and blue properties.
   * @returns {boolean} `true` if the color is valid, otherwise `false`.
   */
  _isValidColor(color) {
    return (
      color &&
      typeof color === "object" &&
      typeof color.red === "number" &&
      typeof color.green === "number" &&
      typeof color.blue === "number" &&
      color.red >= 0 &&
      color.red <= 255 &&
      color.green >= 0 &&
      color.green <= 255 &&
      color.blue >= 0 &&
      color.blue <= 255
    );
  }
}
